import { Cloudinary } from "@cloudinary/url-gen"
import { Resize } from "@cloudinary/url-gen/actions/resize"
import { Avatar, Box, DialogContent, Divider, Modal } from "@mui/material"
import { setFocus } from "@noriginmedia/norigin-spatial-navigation"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { ProgressSpinner } from "src/components/ProgressSpinner"

import { DGText } from "../../../../components/common/DGText"
import { Image } from "../../../../components/common/Image"
import emptyAvatar from "../../../../components/images/empty_avatar.png"
import emptyGroup from "../../../../components/images/empty_group.png"
import empty from "../../../../components/images/empty.png"
import { Label } from "../../../../components/Sliders/Teaser"
import { useModalDismiss } from "../../../../hooks/use-modal-dismiss"
import { useModalScroll } from "../../../../hooks/use-modal-scroll"
import useSdk, {
  ErrorLevel,
  InfoModalQuery,
  PictureType,
} from "../../../../hooks/use-sdk"
import useTranslate from "../../../../hooks/use-translate"
import { ccFactor, s } from "../../../../utilities/common"
import { CloudinaryConf } from "../../../../utilities/common"
import { theme, TI } from "../../../../utilities/theme"
import { usePauseFocus } from "../../../../utilities/use-pause-focus"
import { DetailFocusKeys } from "../../detail-focus-keys"
import { useDetailStore } from "../../DetailStore"

import { getBio, useCredits, useMetaInfo } from "./common"

/*
 EditorialPlaylists have contextually more than one info page, so we pass
 if with the albumId prop
 */
export const InfoModal = ({
  open,
  albumId,
}: {
  open: boolean
  albumId?: string
}) => {
  return open ? <Screen albumId={albumId} /> : null
}

const cld = new Cloudinary(CloudinaryConf)

const teaserImage = (
  data: InfoModalQuery["node"],
  overridePictureType?: PictureType,
) =>
  data?.__typename === "Video" ||
  data?.__typename === "VodConcert" ||
  data?.__typename === "LiveConcert" ||
  data?.__typename === "Album" ||
  data?.__typename === "Artist" ||
  data?.__typename === "Partner" ||
  data?.__typename === "Group" ||
  data?.__typename === "Epoch" ||
  data?.__typename === "Genre"
    ? cld
        .image(
          data.pictures.filter((i) => {
            if (overridePictureType !== undefined)
              return i.type === overridePictureType
            return (
              i.type ===
              (data.__typename === "Album"
                ? "COVER"
                : data.__typename === "Partner"
                  ? "LOGO"
                  : "TEASER")
            )
          })[0]?.url,
        )
        .resize(Resize.scale().width(s(ccFactor(587), true)))
        .format("jpg")
        .setDeliveryType("fetch")
        .toURL()
    : undefined

const InfoImage = ({ node }: { node: InfoModalQuery["node"] }) => {
  if (!node) return null
  if (
    node.__typename === "Artist" ||
    node.__typename === "Group" ||
    node.__typename === "Partner" ||
    node.__typename === "Epoch" ||
    node.__typename === "Genre"
  )
    return (
      <Avatar
        src={teaserImage(node)}
        sx={{
          height: s("260px"),
          width: s("260px"),
          backgroundRepeat: "no-repeat",
          backgroundImage: `url(${
            node.__typename === "Partner"
              ? empty
              : node.__typename === "Group"
                ? emptyGroup
                : emptyAvatar
          })`,
          backgroundPosition: "center",
          backgroundSize:
            (node.__typename === "Partner"
              ? "240"
              : node.__typename === "Group"
                ? s("188px")
                : "102") + "px",
          backgroundColor: theme.palette.secondary.main,
        }}
      />
    )
  if (
    node.__typename === "Video" ||
    node.__typename === "Album" ||
    node.__typename === "VodConcert" ||
    node.__typename === "LiveConcert"
  )
    return (
      <Image
        hasTransparentFallback
        src={teaserImage(
          node,
          node.__typename !== "Album" ? "TEASER_SQUARE" : undefined,
        )}
        sx={{
          height: s("258px"),
          width: s("258px"),
          backgroundRepeat: "no-repeat",
          backgroundImage: `url(${empty})`,
          backgroundPosition: "center",
          backgroundSize: s("240px"),
          backgroundColor: theme.palette.secondary.main,
          borderRadius: s("8px"),
        }}
      />
    )
  return null
}

const Screen = ({ albumId }: { albumId?: string }) => {
  const callback = () => {
    setFocus(DetailFocusKeys.InfoButton)
    setInfoOpen(false)
  }
  const sdk = useSdk(ErrorLevel.WithPrompt, callback)
  const t = useTranslate()
  useModalDismiss(() => callback())
  const setInfoOpen = useDetailStore((state) => state.setInfoOpen)
  const detailContent = useDetailStore((state) => state.content)
  const [header, setHeader] = useState<{
    title?: string
    subtitle?: string
    liveDates?: string
    label?: string
  }>()
  const [bio, setBio] = useState<
    { title?: string; content?: string } | undefined
  >()
  const [data, setData] = useState<InfoModalQuery>()
  const scrollRef = useRef<HTMLElement>(null)
  const credits = useCredits(data?.node)

  useModalScroll(scrollRef)

  useEffect(() => {
    if (albumId) sdk.infoModal({ nodeId: albumId }).then((res) => setData(res))
    else if (detailContent)
      sdk.infoModal({ nodeId: detailContent.id }).then((res) => setData(res))
  }, [detailContent])

  const meta = useMetaInfo({ node: data?.node })

  const ariaLabel = useMemo(
    () =>
      [
        header?.label,
        header?.title,
        header?.subtitle,
        header?.liveDates,
        ...(credits?.map(
          (c) => `${t(c.titleTranslationKey)}: ${c.values.join(", ")}`,
        ) ?? []),
        meta?.join(", "),
        bio?.title,
        bio?.content,
      ]
        .filter((item) => !!item)
        .join(", "),
    [header, credits, meta, bio],
  )
  const FocusTrap = usePauseFocus(ariaLabel)

  useEffect(() => {
    if (data && data.node) {
      const node = data.node
      setHeader({
        title:
          node.__typename === "VodConcert" ||
          node.__typename === "Album" ||
          node.__typename === "Video" ||
          node.__typename === "LiveConcert" ||
          node.__typename === "Epoch" ||
          node.__typename === "Genre"
            ? node.title
            : node.__typename === "Artist" ||
                node.__typename === "Group" ||
                node.__typename === "Partner"
              ? node.name
              : undefined,
        subtitle:
          node.__typename === "VodConcert" ||
          node.__typename === "Video" ||
          node.__typename === "LiveConcert" ||
          node.__typename === "Epoch"
            ? node.subtitle
            : node.__typename === "Album"
              ? node.artistAndGroupDisplayInfo
              : node.__typename === "Artist"
                ? node.roles.map((r) => r.displayName).join(", ")
                : node.__typename === "Partner" || node.__typename === "Group"
                  ? node.typeDisplayName
                  : undefined,
        liveDates:
          data.node.__typename === "Artist" &&
          data.node.dateOfBirth &&
          data.node.dateOfDeath
            ? `${data.node.dateOfBirth} - ${data.node.dateOfDeath}`
            : undefined,
        label:
          node.__typename === "VodConcert" || node.__typename === "Video"
            ? node.typeDisplayName
            : undefined,
      })
      setBio(getBio({ node }))
    }
  }, [data])

  return (
    <Modal
      open={true}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.variant,
        position: "relative",
        "& > *": { transition: "none !important" },
      }}
    >
      <DialogContent
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: theme.palette.primary.variant,
          position: "absolute",
          overflowY: "visible",
        }}
      >
        <FocusTrap />
        {data && header ? (
          <Box
            ref={scrollRef}
            style={{ transition: "transform 0.25s linear" }}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              willChange: "transform",
            }}
          >
            <Box
              sx={{
                width: s("1139px"),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pt: s("147px"),
              }}
            >
              <InfoImage node={data.node} />

              <Box
                sx={{
                  mt: s("37px"),
                  mb: s("90px"),
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                  fontVariantNumeric: "oldstyle-nums",
                  fontFeatureSettings: "onum",
                }}
              >
                {header.label ? (
                  <Box sx={{ mb: s("24px") }}>
                    <Label
                      shadow
                      backgroundColor={theme.palette.white[70]}
                      color={"black"}
                    >
                      {header.label}
                    </Label>
                  </Box>
                ) : null}
                {header.title && (
                  <DGText
                    tx={TI.H05tv}
                    sx={{
                      color: theme.palette.white.main,
                      mb: s("8px"),
                    }}
                  >
                    {header.title}
                  </DGText>
                )}
                {header.subtitle && (
                  <DGText
                    tx={TI.H07tv}
                    sx={{
                      color: theme.palette.white.main,
                    }}
                  >
                    {header.subtitle}
                  </DGText>
                )}
                {header.liveDates ? (
                  <DGText
                    tx={TI.Subheadline05tv}
                    sx={{
                      mt: s("32px"),
                      color: theme.palette.white[70],
                    }}
                  >
                    {header.liveDates}
                  </DGText>
                ) : null}
              </Box>
              {credits && (
                <>
                  <Divider
                    sx={{
                      width: "100%",
                      borderColor: theme.palette.white[20],
                    }}
                  />
                  <Box
                    sx={{
                      pt: s("64px"),
                      pb: s("90px"),
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      width: "100%",
                      "& > :not(:last-child)": {
                        mb: s("36px"),
                      },
                    }}
                  >
                    {credits.map((group, groupIndex) => (
                      <Box key={groupIndex}>
                        <DGText
                          tx={TI.Subheadline05tv}
                          sx={{
                            color: theme.palette.white.main,
                          }}
                        >
                          {t(group.titleTranslationKey)}
                        </DGText>
                        <DGText
                          tx={TI.Subheadline05tv}
                          sx={{
                            color: theme.palette.white.grey,
                          }}
                        >
                          {group.values.join(", ")}
                        </DGText>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
              {meta && meta.length > 0 && (
                <>
                  <Divider
                    sx={{
                      width: "100%",
                      borderColor: theme.palette.white[20],
                    }}
                  />
                  <Box
                    sx={{
                      pt: s("64px"),
                      pb: s("90px"),
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    {meta.map((group, groupIndex) => (
                      <Box
                        sx={{
                          "& > :not(:last-child)": {
                            mb: s("36px"),
                          },
                        }}
                        key={groupIndex}
                      >
                        {group.map((value, valueIndex) => (
                          <DGText
                            tx={TI.Subheadline05tv}
                            sx={{
                              color: theme.palette.white.grey,
                            }}
                            key={valueIndex}
                          >
                            {value}
                          </DGText>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
              {bio && (
                <>
                  <Divider
                    sx={{
                      width: "100%",
                      borderColor: theme.palette.white[20],
                    }}
                  />
                  <Box sx={{ pt: s("64px"), pb: s("90px") }}>
                    {bio.content && (
                      <DGText
                        tx={TI.Subheadline06tv}
                        sx={{
                          color: theme.palette.white.main,
                          whiteSpace: "pre-wrap",
                          fontVariantNumeric: "oldstyle-nums",
                          fontFeatureSettings: "onum",
                        }}
                      >
                        {bio.content}
                      </DGText>
                    )}
                  </Box>
                </>
              )}
              {!bio && !credits && (!meta || meta.length === 0) ? (
                <Divider
                  sx={{ width: "100%", borderColor: theme.palette.white[20] }}
                />
              ) : null}
            </Box>
          </Box>
        ) : (
          <ProgressSpinner />
        )}
      </DialogContent>
    </Modal>
  )
}
